'use client';

import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { useLang, Lang } from './LanguageContext';

export default function NavBar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { key: 'nav_home', href: '#hero' },
    { key: 'nav_destinations', href: '#destinations' },
    { key: 'nav_uzbekistan', href: '#uzbekistan' },
    { key: 'nav_about', href: '#why' },
    { key: 'nav_contact', href: '#contact' },
  ];

  const langs: { code: Lang; label: string }[] = [
    { code: 'ru', label: 'RU' },
    { code: 'en', label: 'EN' },
    { code: 'uz', label: 'UZ' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'py-3 backdrop-blur-xl border-b border-gold/10' :'py-5'
        }`}
        style={{
          background: scrolled
            ? 'rgba(13, 27, 42, 0.92)'
            : 'transparent',
        }}
      >
        <div className="max-w-8xl mx-auto px-6 md:px-10 flex items-center justify-between">
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-3 group">
            <AppLogo size={36} />
            <span
              className="font-serif font-bold text-lg tracking-tight text-cream"
              style={{ fontFamily: 'Fraunces, serif' }}
            >
              AirDubai
              <span className="text-shimmer ml-1">Travel</span>
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a key={link.key} href={link.href} className="nav-link">
                {t(link.key)}
              </a>
            ))}
          </div>

          {/* Right controls */}
          <div className="flex items-center gap-3">
            {/* Language switcher */}
            <div className="flex items-center gap-1 bg-white/5 rounded-lg px-2 py-1">
              {langs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`lang-btn ${lang === l.code ? 'active' : ''}`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {/* Book CTA — desktop */}
            <a href="#contact" className="btn-primary hidden lg:inline-flex text-xs py-2.5 px-5">
              {t('nav_book')}
            </a>

            {/* Hamburger */}
            <button
              onClick={() => setMenuOpen(true)}
              className="lg:hidden flex flex-col gap-1.5 p-2"
              aria-label="Open menu"
            >
              <span className="block w-6 h-0.5 bg-cream/80" />
              <span className="block w-4 h-0.5 bg-gold" />
              <span className="block w-6 h-0.5 bg-cream/80" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`mobile-menu ${menuOpen ? 'open' : ''} flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
          <span
            className="font-serif font-bold text-xl text-cream"
            style={{ fontFamily: 'Fraunces, serif' }}
          >
            AirDubai<span className="text-shimmer">Travel</span>
          </span>
          <button
            onClick={() => setMenuOpen(false)}
            className="p-2 text-cream/60 hover:text-gold transition-colors"
            aria-label="Close menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 flex flex-col justify-center px-8 gap-2">
          {navLinks.map((link, i) => (
            <a
              key={link.key}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="py-4 border-b border-white/5 text-2xl font-serif font-light text-cream/80 hover:text-gold transition-colors"
              style={{
                fontFamily: 'Fraunces, serif',
                animationDelay: `${i * 0.05}s`,
              }}
            >
              {t(link.key)}
            </a>
          ))}
        </div>

        <div className="px-8 pb-10">
          {/* Language switcher mobile */}
          <div className="flex gap-2 mb-6">
            {langs.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); }}
                className={`lang-btn text-sm py-2 px-4 rounded-lg border ${
                  lang === l.code
                    ? 'bg-gold text-navy border-gold' :'border-white/10 text-cream/50 hover:border-gold/40'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <a href="#contact" onClick={() => setMenuOpen(false)} className="btn-primary w-full justify-center">
            {t('nav_book')}
          </a>
        </div>
      </div>
    </>
  );
}